import { isAxiosError } from 'axios'

interface ErrorBody {
  message?: string
  errors?: Record<string, string[]>
}

/** First message from a Laravel 422 `errors` bag, else the top-level `message`; null when the body has neither. */
export function firstValidationMessage(error: unknown): string | null {
  if (!isAxiosError(error)) return null
  const data = error.response?.data as ErrorBody | undefined
  const first = data?.errors ? Object.values(data.errors).flat()[0] : undefined
  return first || data?.message || null
}

/**
 * One readable line for a failed request: the first 422 field message, then the server's
 * `message`, with 429 throttling spelled out. `fallback` covers network/unknown failures.
 * Keeps `catch` blocks typed as `unknown` instead of `any`.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error) && error.response?.status === 429) {
    const wait = error.response.headers?.['retry-after']
    return wait ? `Too many requests. Try again in ${wait} seconds.` : 'Too many requests. Wait a minute and try again.'
  }
  return firstValidationMessage(error) ?? fallback
}

/**
 * `apiErrorMessage` for requests made with `responseType: 'blob'`. Axios applies that
 * responseType to error responses too, so the JSON error body arrives as a Blob and
 * has to be read before the message can be pulled out of it.
 */
export async function apiBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const data = isAxiosError(error) ? error.response?.data : undefined

  if (!(data instanceof Blob)) {
    return apiErrorMessage(error, fallback)
  }

  try {
    return (JSON.parse(await data.text()) as { message?: string }).message ?? fallback
  } catch {
    // Not JSON (an HTML error page, or a truncated body) — nothing useful to show.
    return fallback
  }
}

export function getCsrfToken(): string | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
  return match ? decodeURIComponent(match.split('=')[1]) : null
}
